import React, {useEffect, useState, useCallback} from 'react'
import {
    useAccessToken,
    useCommerceApi,
    useShopperBasketsMutationHelper
} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useToast} from '../../hooks/use-toast'
import {useQueryClient} from '@tanstack/react-query'
import LoadingSpinner from '../loading-spinner'
import {useIntl} from 'react-intl'
import useTagManager from '../../hooks/use-tag-manager'
import {getSource} from '../../utils/product-utils'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {logBasketErrorToSsr} from '../../utils/log-basket-error-to-ssr'

const IAdvzieScript = () => {
    const {getTokenWhenReady} = useAccessToken()
    const api = useCommerceApi()
    const {site} = useMultiSite()
    const intl = useIntl()
    const tagManager = useTagManager()
    const queryClient = useQueryClient()
    const {data: basket} = useCurrentBasket()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const {
        app: {commerceAPI}
    } = getConfig()
    const {storefrontPreferences} = commerceAPI.storefront || {}
    const [showSpinner, setShowSpinner] = useState(false)
    let enableIAdvizeAddToCart = false

    try {
        if (storefrontPreferences) {
            const customPrefsAsJson = JSON.parse(storefrontPreferences)
            enableIAdvizeAddToCart = customPrefsAsJson.enableIAdvizeAddToCart
        }
    } catch (e) {
        console.log(e.message)
    }
    /** ---- Toasts ---- **/
    const showToast = useToast()
    const toastMessages = {
        added: intl.formatMessage({
            id: 'iadvize.product.added',
            defaultMessage: 'Product added to cart'
        }),
        error: intl.formatMessage({
            id: 'iadvize.product.unable.to.add',
            defaultMessage: 'Unable to add the product'
        })
    }
    const notify = useCallback(
        (status, message) => showToast({title: message, status}),
        [showToast]
    )

    /** ---- Keep window basketId in sync ---- **/
    useEffect(() => {
        if (basket?.basketId) {
            window.basketId = basket.basketId
            localStorage.setItem('basketId', basket.basketId)
        }
    }, [basket?.basketId, basket?.productItems?.length])

    const {clientConfig} = api.shopperExperience

    const triggerGtmEvent = (product, source) => {
        tagManager.iAdvizeAddToBag(product, source)
    }
    /** ---- Basket mutation handler (keeps resolve; no async executor) ---- **/
    const addProductToBasket = useCallback(
        (productId) =>
            new Promise((resolve) => {
                setShowSpinner(true)
                ;(async () => {
                    try {
                        const source = getSource()
                        if (window.basketId) {
                            // Basket exists -> use 'fetch'
                            const token = await getTokenWhenReady()
                            const response = await fetch(
                                `${clientConfig.proxy}/checkout/shopper-baskets/v1/organizations/${clientConfig.parameters.organizationId}/baskets/${window.basketId}/items?siteId=${site?.id}`,
                                {
                                    method: 'POST',
                                    headers: {
                                        Authorization: `Bearer ${token}`,
                                        'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify([
                                        {productId, quantity: 1, c_isFromIadvize: true}
                                    ])
                                }
                            )

                            if (response.ok) {
                                notify('success', toastMessages.added)
                                await queryClient.invalidateQueries()
                                const basketResponse = await response.json()
                                const product = basketResponse?.productItems?.find(
                                    (i) => i?.productId === productId
                                )
                                triggerGtmEvent(product, source)
                                resolve(true)
                            } else {
                                const errResponse = await response.json()
                                logger.error('ATCXL Error adding product to basket', {
                                    namespace: 'IAdvize.addProductToBasket',
                                    additionalProperties: {
                                        errResponse,
                                        basketId: window.basketId,
                                        productId
                                    }
                                })
                                logBasketErrorToSsr('IAdvize.addProductToBasket', {
                                    errResponse,
                                    basketId: window.basketId,
                                    productId
                                })
                                notify('error', toastMessages.error)
                                resolve(false)
                            }
                        } else {
                            // No basket yet -> use helper/hook
                            const basketResponse = await addItemToNewOrExistingBasket([
                                {productId, quantity: 1, c_isFromIadvize: true}
                            ])
                            if (basketResponse) {
                                notify('success', toastMessages.added)
                                const product = basketResponse?.productItems?.find(
                                    (i) => i?.productId === productId
                                )
                                triggerGtmEvent(product, source)
                                resolve(true)
                            } else {
                                notify('error', toastMessages.error)
                                resolve(false)
                            }
                        }
                    } catch (err) {
                        const errResponse = err?.response ? await err.response.json() : null
                        logger.error('ATCXL Error adding product to basket', {
                            namespace: 'IAdvize.addProductToBasket',
                            additionalProperties: {
                                err,
                                errResponse,
                                basketId: window.basketId,
                                productId
                            }
                        })
                        logBasketErrorToSsr('IAdvize.addProductToBasket', {
                            err,
                            errResponse,
                            basketId: window.basketId,
                            productId
                        })
                        notify('error', toastMessages.error)
                        resolve(false)
                    } finally {
                        setShowSpinner(false)
                    }
                })()
            }),
        [
            api.shopperExperience.clientConfig.parameters.organizationId,
            addItemToNewOrExistingBasket,
            clientConfig.proxy,
            getTokenWhenReady,
            notify,
            queryClient,
            site?.id,
            toastMessages.added,
            toastMessages.error
        ]
    )

    /** ---- iAdvize integration ---- **/
    useEffect(() => {
        if (window.__iAdvizeHookRegistered || !enableIAdvizeAddToCart) return
        window.__iAdvizeHookRegistered = true

        window.iAdvizeInterface = window.iAdvizeInterface || []
        window.iAdvizeInterface.push((iAdvize) => {
            iAdvize.labs().onAddToCart((productId) => addProductToBasket(productId))
        })
    }, [addProductToBasket])

    return showSpinner ? <LoadingSpinner position="fixed" top="auto" /> : null
}

export default IAdvzieScript
